import type { TFunction } from 'i18next';
import DeleteIcon from '../../assets/icons/delete.svg?react';
import EditIcon from '../../assets/icons/edit.svg?react';
import type { InterventionRoomPropertyResponse, InterventionRoomResponse } from '../../api/generated';
import { EditableCell, EditingActions, PropertiesCell, tertiaryIconButtonStyle } from '../UI';
import type { TableColumn } from '../UI';
import { Button } from '../UI/base/Button';
import { CapacityLimitCell } from './CapacityLimitCell';
import columnStyles from './interventionRoomColumns.module.css';

interface InterventionRoomColumnCallbacks {
  editingRoomId: string | null;
  editingName: string;
  editingCapacityLimit: string;
  openPickerRoomId: string | null;
  allRoomProperties: InterventionRoomPropertyResponse[];
  onStartEditName: (roomId: string) => void;
  onNameChange: (value: string) => void;
  onCapacityLimitChange: (value: string) => void;
  onSaveName: () => void;
  onCancelEdit: () => void;
  onOpenPicker: (roomId: string) => void;
  onClosePicker: () => void;
  onAddProperty: (roomId: string, propertyId: string) => void;
  onRemoveProperty: (roomId: string, propertyId: string) => void;
  onDelete: (room: InterventionRoomResponse) => void;
}

export const getInterventionRoomColumns = (
  t: TFunction,
  cb: InterventionRoomColumnCallbacks
): TableColumn<InterventionRoomResponse>[] => [
  {
    key: 'name',
    header: t('settings-col-intervention-room-name'),
    width: 237,
    cellStyle: { whiteSpace: 'nowrap' },
    render: (room) => (
      <EditableCell
        value={room.description}
        isEditing={cb.editingRoomId === room.id}
        name={cb.editingName}
        onNameChange={cb.onNameChange}
        onSave={cb.onSaveName}
        onCancel={cb.onCancelEdit}
      />
    )
  },
  {
    key: 'capacityLimit',
    header: t('settings-col-capacity-limit'),
    width: 150,
    cellStyle: { whiteSpace: 'nowrap' },
    render: (room) => (
      <CapacityLimitCell
        value={room.capacityLimit}
        isEditing={cb.editingRoomId === room.id}
        editingValue={cb.editingCapacityLimit}
        onChange={cb.onCapacityLimitChange}
        onSave={cb.onSaveName}
        onCancel={cb.onCancelEdit}
      />
    )
  },
  {
    key: 'needs',
    header: t('settings-col-intervention-room-needs'),
    cellStyle: { flex: '1 1 0', flexBasis: 0, minWidth: 200 },
    render: (room) => (
      <PropertiesCell
        assignedProperties={room.interventionRoomProperties}
        allProperties={cb.allRoomProperties}
        emptyLabel={t('intervention-room-need-no-results')}
        isPickerOpen={cb.openPickerRoomId === room.id}
        isEditing={cb.editingRoomId === room.id}
        onOpenPicker={() => cb.onOpenPicker(room.id)}
        onClosePicker={cb.onClosePicker}
        onAdd={(propertyId) => cb.onAddProperty(room.id, propertyId)}
        onRemove={(propertyId) => cb.onRemoveProperty(room.id, propertyId)}
      />
    )
  },
  {
    key: 'edit',
    header: t('settings-col-edit'),
    width: 100,
    headerAlign: 'center',
    cellStyle: { alignItems: 'center' },
    render: (room) => {
      const isEditingThisRow = cb.editingRoomId === room.id;

      if (isEditingThisRow) {
        const canSave = cb.editingName.trim().length > 0;
        return (
          <EditingActions canSave={canSave} onSave={cb.onSaveName} onCancel={cb.onCancelEdit} />
        );
      }

      return (
        <Button
          type="tertiary"
          onClick={() => cb.onStartEditName(room.id)}
          styles={{ root: tertiaryIconButtonStyle }}
        >
          <EditIcon className={columnStyles.actionIcon} />
        </Button>
      );
    }
  },
  {
    key: 'delete',
    header: t('settings-col-delete'),
    width: 100,
    headerAlign: 'center',
    cellStyle: { alignItems: 'center' },
    render: (room) => (
      <Button
        type="tertiary"
        onClick={() => cb.onDelete(room)}
        styles={{ root: tertiaryIconButtonStyle }}
      >
        <DeleteIcon className={columnStyles.actionIcon} />
      </Button>
    )
  }
];
